import { RefusedError, SocialAuthError, refusalKey } from '@versesverse/core';
import type { Translate } from './lang';
import type { Strings } from './strings';

/**
 * WHAT TO PUT ON THE SCREEN WHEN THE SERVER SAID NO — in the reader's language where we have one.
 *
 * Every refusal this server writes is English, because the server does not know who is reading; and
 * every screen here used to render `caught.message`, so a Hebrew reader who mistyped a room's
 * passphrase read an English sentence in the middle of a Hebrew page. The server names its
 * refusals now (`REFUSAL_REASONS` in core) and this turns the name into words.
 *
 * A NAMED refusal reads from the dictionary, with the server's numbers filled into whatever shape
 * that language puts them in — which is why the numbers travel beside the sentence rather than
 * inside it: "this room is full (8)" and its Hebrew do not agree on where the 8 goes.
 *
 * An UNNAMED one keeps the server's English sentence. A true sentence in the wrong language beats a
 * vague one in the right language, and a server newer than its clients must still be able to
 * explain itself — the same bargain the phrase dictionary makes with English.
 */
export function refusalSays(t: Translate, refused: RefusedError): string {
  const key = refusalKey(refused);
  return key === null ? refused.message : t(key, { ...refused.values });
}

/**
 * The same, for the screens that ask "was this a refusal at all?" in one expression.
 *
 * `otherwise` is what to say for anything that is NOT a refusal — a dropped connection, a bug —
 * because `Failed to fetch` is not a sentence to show anybody.
 *
 * AND THE THING THAT WAS NOT A REFUSAL IS WRITTEN DOWN, which is the only side effect in this file
 * and is here for a reason: this is the one place in either app that can tell a sentence the server
 * DELIBERATELY WROTE from something that merely threw. Thirteen screens call it, and every one of
 * them used to drop the second kind on the floor — so a reader reporting "it said it could not do
 * that" left nobody able to say why, and a browser gate watching a room refuse to draw reported
 * that the page had said NOTHING AT ALL.
 *
 * Only the fall-through, never a refusal: a refused post is the product working, and a log line per
 * refusal is noise that hides the one line that matters.
 */
export function refusalWords(t: Translate, caught: unknown, otherwise: keyof Strings): string {
  if (caught instanceof RefusedError) return refusalSays(t, caught);
/**
 * AN EXPIRED SESSION IS NOT A SURPRISE, and it used to be shown as one.
 *
 * A 401 arriving here means the screen has no branch of its own for it — four on this client do not
 * — and what the reader was shown was the caller's generic fallback: *"that did not work — try
 * again."* **Try again is advice that cannot work.** Nothing this reader presses will work until
 * they sign in, and this layer treats advice that cannot work as a defect wherever else it appears:
 * a retired Discord channel is told *"that webhook is gone, make a new one"* rather than *"reconnect
 * it"*, for exactly this reason.
 *
 * HERE RATHER THAN IN THE FOUR SCREENS, which is the shape `claimForAnnounce` argues for: a rule
 * each of them could forget is a rule that breaks by whichever path nobody checked. A screen that
 * handles the 401 itself never reaches this line, so nothing is taken away from the ones that do.
 *
 * AND IT NO LONGER WARNS FOR ONE. The log line below is for things nobody expected, and a 401 is
 * catalogued in §0 — it was the loudest entry in a channel whose whole value is being quiet. What
 * the warning was standing in for was a reader being told something false, and that is fixed above
 * rather than logged about.
 *
 * NOT the central re-read §0 asks Ariel about: this says the truth when a 401 REACHES a screen, and
 * says nothing about the app noticing by itself while a tab sits idle.
 */
  if (caught instanceof SocialAuthError) return t('auth.sessionEnded');
  console.warn('[social] something that was not a written refusal reached a screen:', caught);
  return t(otherwise);
}
